'use client';

import Image from 'next/image';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import { isPaywallArticle, isProOnlyPost } from '@benzinga/article-manager';
import { EdgeBadge, ProBadge, SectionTitle } from '@benzinga/core-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import type { SimpleNewsQueryAndOptions } from '@benzinga/internal-news-manager';
import { ContentFeed, DefaultNewsElement, PostElapsed } from '@benzinga/news';
import { Ticker } from '@benzinga/ticker-ui';
import { convertToRelativeUrl, formatImageUrl } from '@benzinga/utils';

const storyHref = (story: StoryObject): string => {
  if (isProOnlyPost(story)) {
    return '#';
  }
  if (story.url) {
    return convertToRelativeUrl(story.url);
  }
  return `/article/${story.id}`;
};

const storyTickers = (story: StoryObject): string[] => {
  const names = (story.stocks ?? story.tickers ?? []).map(ticker => ticker.name).filter(Boolean);
  return [...new Set(names)].slice(0, 2);
};

const Headline = ({ className, story }: { className: string; story: StoryObject }) => {
  const showProBadge = isProOnlyPost(story);
  const showEdgeBadge = isPaywallArticle(story);
  return (
    <h3 className={className}>
      {showProBadge || showEdgeBadge ? (
        <span className="mr-1.5 inline-block scale-90 align-middle">
          {showProBadge ? <ProBadge /> : EdgeBadge}
        </span>
      ) : null}
      <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(story.title ?? '') }} />
    </h3>
  );
};

const StoryMeta = ({ story }: { story: StoryObject }) => {
  const tickers = storyTickers(story);
  const published = story.created || story.updated;
  return (
    <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1">
      {tickers.map(symbol => (
        <Ticker hidePopup key={symbol} symbol={symbol} />
      ))}
      {published ? (
        <PostElapsed className="text-xs text-slate-400" created={published} dateType="relative" />
      ) : null}
    </div>
  );
};

const Thumb = ({
  alt,
  className,
  sizes,
  src,
}: {
  alt: string;
  className: string;
  sizes: string;
  src: string;
}) => (
  <div className={`relative overflow-hidden bg-slate-100 outline outline-1 outline-black/10 ${className}`}>
    <Image alt={alt} fill sizes={sizes} src={src} style={{ objectFit: 'cover' }} />
  </div>
);

const ChannelNewsCards = ({
  className,
  stories,
  title,
}: {
  className?: string;
  stories: StoryObject[];
  title?: string;
}) => (
  <section className={className}>
    {title ? <h2 className="channel-news-cards__title">{title}</h2> : null}
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {stories.map(story => {
        const image = formatImageUrl(story);
        const href = storyHref(story);
        const imageAlt = story.assets?.[0]?.title ?? '';
        return (
          <article className="channel-news-cards__item" key={story.id}>
            {image ? (
              <a className="block" href={href} tabIndex={-1}>
                <Thumb
                  alt={imageAlt}
                  className="aspect-[16/10] w-full"
                  sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, (max-width: 1279px) 33vw, 25vw"
                  src={image}
                />
              </a>
            ) : null}
            <div className="flex min-h-0 flex-1 flex-col gap-2 p-3.5">
              <a className="min-w-0 no-underline" href={href}>
                <Headline
                  className="line-clamp-3 text-[15px] font-bold leading-snug text-[#192940] hover:text-bzblue-700"
                  story={story}
                />
              </a>
              <StoryMeta story={story} />
            </div>
          </article>
        );
      })}
    </div>
  </section>
);

type ChannelNewsFeedProps = {
  className?: string;
  stories: StoryObject[];
  title?: string;
} & ({ layout?: 'feed'; query: SimpleNewsQueryAndOptions } | { layout: 'cards'; query?: undefined });

export const ChannelNewsFeed = (props: ChannelNewsFeedProps) => {
  if (props.stories.length === 0) {
    return null;
  }

  if (props.layout === 'cards') {
    return <ChannelNewsCards className={props.className} stories={props.stories} title={props.title} />;
  }

  return (
    <ContentFeed
      className={props.className}
      {...(props.title
        ? {
            feedHeaderElement: (heading: string) => (
              <SectionTitle className="mb-4" level={2}>
                {heading}
              </SectionTitle>
            ),
            title: props.title,
          }
        : {})}
      isInfinite={false}
      limit={10}
      loadMore
      newsItemElement={(node, index) => (
        <DefaultNewsElement key={`${node.id}-${index}`} node={node as StoryObject} />
      )}
      nodes={props.stories}
      query={props.query}
    />
  );
};
